import * as React from "react";

import twMerge from "@v1/components/common/designSystem/utils/twMerge";
import ErrorMessage from "@v1/components/common/ErrorMessage/ErrorMessage";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import Description from "@v1/components/designSystem/label/Description";
import DeprecatedLabel from "@v1/components/designSystem/label/Label";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

type BaseLabeledControlProps = {
  label?: string | React.ReactNode;
  id?: string;
  error?: React.ReactNode | null;
  className?: string;
  containerClassName?: string;
  size?: "base" | "sm" | "xs";
  description?: string;
  descriptionPosition?: "top" | "bottom";
  onClick?: React.MouseEventHandler<HTMLLabelElement>;
  required?: boolean;
};

type DefaultLabeledControlProps = {
  variant?: "default";
  labelActionText?: string | React.ReactNode;
  labelAction?: () => void;
};

type InlineLabeledControlProps = {
  variant: "inline";
};

type LabeledControlProps = BaseLabeledControlProps &
  (DefaultLabeledControlProps | InlineLabeledControlProps);

type VariantLabeledControlProps = {
  labelActionText?: string | React.ReactNode;
  labelAction?: () => void;
};

/**
 * @deprecated DEPRECATED - Use LabeledControl from @replohq/lattice/blocks/labeled-control/LabeledControl instead.
 *
 * Migration notes:
 * - containerClassName -> className (layout classes) or UNSAFE_className (other classes)
 * - error must be string in Field (not ReactNode)
 * - labelActionText/labelAction -> labelAction (pass ReactNode directly)
 */
const DeprecatedLabeledControl: React.FC<
  React.PropsWithChildren<LabeledControlProps>
> = ({
  id,
  label,
  children,
  error,
  containerClassName,
  className = "",
  onClick,
  size = "base",
  description,
  variant = "default",
  required = false,
  descriptionPosition = "bottom",
  ...rest
}) => {
  const variantProps = exhaustiveSwitch(
    { ...rest, variant },
    "variant",
  )<VariantLabeledControlProps>({
    default: (props) => ({
      labelActionText: props.labelActionText,
      labelAction: props.labelAction,
    }),
    inline: () => ({}),
  });
  const { labelActionText, labelAction } = variantProps || {};

  const labelClassName = twMerge(
    "font-medium leading-5 text-default",
    Boolean(onClick) && "cursor-pointer",
    size === "sm" && "text-xs",
    size === "base" && "text-sm",
    size === "xs" && "text-2xs font-medium text-muted",
    className,
  );

  return (
    <div
      className={twMerge(
        "flex flex-col gap-1.5",
        containerClassName,
        variant === "inline" && "flex-row",
      )}
    >
      {/* Note (Noah, 2025-04-22): This extra div with flex-col-reverse makes it so that inputs inside the children
      come first in tab order, which is what we want (very annoying to tab from the email field on the login page
      and get to "forgot password" instead of "password") */}
      <div
        className={twMerge(
          "flex flex-col-reverse gap-1.5",
          variant === "inline" && "flex-row",
        )}
      >
        {children}
        <div className="flex flex-col gap-1">
          <div className="flex justify-between gap-1">
            <div className="flex items-center gap-1">
              <DeprecatedLabel
                size={size}
                htmlFor={id}
                className={labelClassName}
                onClick={onClick}
                label={label}
              />
              {required && (
                <span className="text-rose text-xs leading-none relative -top-0.5">
                  *
                </span>
              )}
            </div>
            {labelActionText && (
              <DeprecatedButton
                onClick={labelAction}
                variant="link"
                size={size === "xs" ? "sm" : size}
              >
                {labelActionText}
              </DeprecatedButton>
            )}
          </div>
          {descriptionPosition === "top" && description && (
            <Description description={description} size={size} />
          )}
        </div>
      </div>
      {error && <ErrorMessage error={error} />}
      {description && !error && descriptionPosition === "bottom" && (
        <Description description={description} size={size} />
      )}
    </div>
  );
};

export default DeprecatedLabeledControl;
